import React, { useState } from "react"
import { Table, Alert, Button, Dropdown, Menu, Modal } from "antd"
import { Nullable } from "platform-client/types"
import { isEmpty } from "ramda"
import { DateDisplay } from "components/display/DateDisplay"
import {
  DownOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  LoadingOutlined,
  ReloadOutlined,
  SendOutlined
} from "@ant-design/icons"
import {
  deduplicatePolicyDocuments,
  getDocumentDownloadUrl,
  getDocumentViewUrl,
  getPolicyDocumentRowKey
} from "utils/documents/documents"
import { useDownloadDocumentPdf } from "utils/documents/use-download-document-pdf"
import { useResendDocument } from "contexts/opus/hooks/use-resend-document"
import { ConfirmEmailModal } from "../../../../../../components/ConfirmEmailModal"
import { useRegenerateDocument } from "contexts/opus/hooks/use-regenerate-document"
import {
  DocTypeMappings,
  PolicyCancellationReference,
  supportsDownloadWithoutBreakdown
} from "utils/data/document-mappings"
import { IDocument } from "models/documents/document"

const removeDisplayExtension = (filename: string) =>
  filename.replace(/\.(docx|html|pdf)$/i, "")

export const PolicyDocumentsView: React.FC<IPolicyDocumentsViewProps> = (
  props
) => {
  const [selectedDocument, setSelectedDocument] =
    useState<Nullable<IDocument>>(null)

  const { isResending, resendDocument } = useResendDocument()

  const { isRegenerating, regenerateDocument } = useRegenerateDocument()

  const { downloadDocumentPdf, isDownloading } = useDownloadDocumentPdf({
    entityType: "policy",
    entityId: props.policyId ?? ""
  })

  const resendDoc = async (emailAddress: string) => {
    selectedDocument &&
      (await resendDocument(
        selectedDocument.directDocumentID,
        null,
        props.policyId ?? "",
        emailAddress,
        selectedDocument.referenceID === "avt_doc_p_canx_default"
      ))
    setSelectedDocument(null)
  }

  const documents = deduplicatePolicyDocuments(props.documents)

  const modalRegenerateDocument = (document: IDocument) =>
    Modal.confirm({
      title: "Are you sure you want to regenerate this document?",
      content: "You won't be able to undo this action",
      centered: true,
      okText: "Regenerate",
      onOk: async () => {
        await regenerateDocument(
          null,
          props.policyId ?? "",
          document.referenceID === PolicyCancellationReference
        )
      }
    })

  return (
    <>
      {!isEmpty(documents) ? (
        <>
          <Table
            loading={props?.loading || isDownloading}
            dataSource={documents}
            scroll={{ x: "max-content" }}
            rowKey={getPolicyDocumentRowKey}
            pagination={{
              defaultPageSize: 10,
              hideOnSinglePage: true,
              position: ["bottomCenter"],
              size: "small"
            }}
          >
            <Table.Column
              dataIndex="filename"
              title="Document Name"
              render={(value: string, document: IDocument) => (
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={
                    getDocumentViewUrl(
                      document.documentUrl,
                      document.documentType
                    ) ?? ""
                  }
                >
                  {removeDisplayExtension(value)}
                </a>
              )}
            />
            <Table.Column
              dataIndex="referenceID"
              title="Document Type"
              render={(value: string) => DocTypeMappings[value]}
            />
            <Table.Column
              dataIndex="localDateCreated"
              title="Created"
              render={(value: string) => <DateDisplay date={value} />}
            />
            <Table.Column
              key="tools"
              align="right"
              render={(document: IDocument, _, index) => {
                const hasUrl = typeof document.documentUrl === "string"
                const isPending =
                  document.policyDocumentStatus === "PendingGeneration"
                const isStatic = document.documentType === "Static"
                return (
                  <Dropdown
                    overlay={
                      <Menu>
                        {hasUrl &&
                          !isPending &&
                          document.filename?.endsWith(".html") && (
                            <Menu.Item
                              key="preview"
                              icon={<FileTextOutlined />}
                            >
                              <a
                                href={
                                  getDocumentViewUrl(
                                    document.documentUrl,
                                    document.documentType
                                  ) ?? undefined
                                }
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  color: "inherit",
                                  textDecoration: "none"
                                }}
                              >
                                Preview
                              </a>
                            </Menu.Item>
                          )}
                        {(!isStatic || document.filename?.endsWith(".pdf")) && (
                          <Menu.Item
                            key="pdf"
                            icon={<FilePdfOutlined />}
                            disabled={!hasUrl}
                          >
                            {hasUrl ? (
                              <a
                                href={
                                  getDocumentDownloadUrl(
                                    document.documentUrl,
                                    "pdf"
                                  ) ?? undefined
                                }
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  color: "inherit",
                                  textDecoration: "none"
                                }}
                              >
                                Download as PDF
                              </a>
                            ) : (
                              "PDF"
                            )}
                          </Menu.Item>
                        )}
                        {props.allowDownloadWithoutBreakdown &&
                          supportsDownloadWithoutBreakdown(
                            document.referenceID
                          ) &&
                          (!isStatic ||
                            document.filename?.endsWith(".pdf")) && (
                            <Menu.Item
                              key="pdf-no-premium"
                              icon={
                                isDownloading ? (
                                  <LoadingOutlined />
                                ) : (
                                  <FilePdfOutlined />
                                )
                              }
                              disabled={!props.policyId || isDownloading}
                              onClick={() => downloadDocumentPdf(true)}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  lineHeight: 1.2
                                }}
                              >
                                Download as PDF
                                <br />
                                <span
                                  style={{
                                    color: "rgba(0, 0, 0, 0.45)",
                                    fontSize: 12
                                  }}
                                >
                                  (no premium breakdown)
                                </span>
                              </span>
                            </Menu.Item>
                          )}
                        {props.pdfToDocxConversionEnabled &&
                          (!isStatic ||
                            document.filename?.endsWith("docx")) && (
                            <Menu.Item
                              key="docx"
                              icon={<FileWordOutlined />}
                              disabled={!hasUrl}
                            >
                              {hasUrl ? (
                                <a
                                  href={
                                    getDocumentDownloadUrl(
                                      document.documentUrl,
                                      "docx"
                                    ) ?? undefined
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    color: "inherit",
                                    textDecoration: "none"
                                  }}
                                >
                                  Download as DOCX
                                </a>
                              ) : (
                                "DOCX"
                              )}
                            </Menu.Item>
                          )}
                        {index === 0 && (
                          <Menu.Item
                            key="regenerate"
                            icon={<ReloadOutlined />}
                            disabled={isRegenerating}
                            onClick={() => modalRegenerateDocument(document)}
                          >
                            Regenerate
                          </Menu.Item>
                        )}
                        {props.showSendButton && (
                          <Menu.Item
                            key="send"
                            icon={<SendOutlined />}
                            onClick={() => setSelectedDocument(document)}
                          >
                            Send
                          </Menu.Item>
                        )}
                      </Menu>
                    }
                  >
                    <Button type="primary" size="small">
                      Actions <DownOutlined />
                    </Button>
                  </Dropdown>
                )
              }}
            />
          </Table>
          <ConfirmEmailModal
            emailAddress={props.emailAddress ?? ""}
            title={`Send document ${selectedDocument?.filename}`}
            description="Please provide the email address you would like to send the
          document to."
            isVisible={!!selectedDocument}
            onCancel={(e) => setSelectedDocument(null)}
            onSend={resendDoc}
            isLoading={isResending}
          />
        </>
      ) : (
        <Alert message="There are no documents available." />
      )}
    </>
  )
}

export interface IPolicyDocumentsViewProps {
  policyId?: string
  documents: IDocument[] | undefined
  loading?: boolean
  emailAddress?: string
  pdfToDocxConversionEnabled?: boolean
  showSendButton?: boolean
  allowDownloadWithoutBreakdown?: boolean
}
